import { useState } from 'react';
import { toast } from 'sonner';

import { adminApi } from '@/services/adminApi';

type ExportEndpoint = 'customers' | 'subscriptions' | 'postings' | 'applications' | 'users' | 'admin-actions';

/**
 * useAdminExportCsv — small hook to trigger a CSV download from any of
 * the admin list endpoints. Adds a loading state for the button and
 * surfaces toast feedback. The request includes the Bearer token, so
 * we cannot just window.open — we fetch the Blob and inject a temp
 * <a download> element.
 */
export function useAdminExportCsv(endpoint: ExportEndpoint, filters: Record<string, unknown> = {}) {
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await adminApi.exportCsv(endpoint, filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${endpoint}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${endpoint} CSV downloaded`);
    } catch (err) {
      toast.error((err as Error).message || 'CSV export failed');
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCsv, isExporting };
}
